import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posBackendApi } from '@/lib/api-client';

export interface CreditAccountSummary {
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerType: 'credit' | 'wholesale' | 'retail';
  creditLimit: string;
  currentBalance: string;
  totalCharged: string;
  totalPaid: string;
  pastDueAmount: string;
  oldestChargeAt: string | null;
  lastPaymentAt: string | null;
  // Per-customer aged-receivable breakdown (gross credit charges by charge age),
  // using the same bucket definitions as the aggregate `AgingBuckets` cards.
  bucket_0_30: string;
  bucket_31_60: string;
  bucket_61_90: string;
  bucket_90_plus: string;
}

export interface AgingBuckets {
  bucket_0_30: string;
  bucket_31_60: string;
  bucket_61_90: string;
  bucket_90_plus: string;
}

export interface CreditAccountStatementRow {
  type: 'charge' | 'payment';
  occurredAt: string;
  amount: string;
  reference: string;
  details: Record<string, unknown>;
}

export interface RecordPaymentInput {
  amount: number;
  method: 'cash' | 'card' | 'check' | 'ach';
  referenceNumber?: string;
  notes?: string;
  locationId?: string;
  posTransactionId?: string;
}

const KEY = ['customer-engagement', 'credit-accounts'] as const;

export function useCreditAccountsAging() {
  return useQuery({
    queryKey: [...KEY, 'aging'],
    queryFn: () =>
      posBackendApi.get<{ data: CreditAccountSummary[]; meta: { buckets: AgingBuckets } }>(
        '/customer-engagement/credit-accounts',
      ),
    staleTime: 30_000,
  });
}

export function useCreditStatement(customerId: string | undefined, from?: string, to?: string) {
  return useQuery({
    queryKey: [...KEY, 'statement', customerId, { from, to }],
    queryFn: () =>
      posBackendApi.get<{
        data: CreditAccountStatementRow[];
        meta: { customerId: string; from: string | null; to: string | null };
      }>(`/customer-engagement/credit-accounts/${customerId}/statement`, {
        from,
        to,
      }),
    enabled: !!customerId,
  });
}

export function useRecordPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { customerId: string; input: RecordPaymentInput }) =>
      posBackendApi.post<{ data: { id: string; newBalance: string } }>(
        `/customer-engagement/credit-accounts/${vars.customerId}/payments`,
        vars.input,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['customer-engagement', 'customers'] });
      qc.invalidateQueries({ queryKey: [...KEY, 'statement', vars.customerId] });
    },
  });
}
